import React, { useEffect } from 'react';
import { rentalExpiryBucket } from '../../utils/rentalExpiry';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../components/ui/Layout';
import { usePermission } from '../../hooks/usePermission';
import {
  RiFileListLine,
  RiCheckLine,
  RiCloseLine,
  RiRefreshLine,
  RiCalendarLine,
  RiAlertLine,
  RiTimeLine,
  RiArrowRightSLine,
  RiArrowUpLine,
  RiArrowDownLine,
  RiTruckLine,
  RiFileTextLine,
  RiMoneyDollarBoxLine,
  RiPieChartLine,
  RiLineChartLine
} from 'react-icons/ri';
import { useQuery } from '@tanstack/react-query';
import rentalService, { RentalStatus } from '../../services/rentalService';
import { getRentalStatusUrlParam } from '../../utils/rentalStatusLabel';
import { showAlert } from '../../utils/alert';
import EmptyState from '../../components/ui/EmptyState';
import { RefreshButton } from '../../components/ui/LoadingButton';
import {
  StatCardSkeleton,
  ChartSkeleton,
  TableSkeleton
} from '../../components/ui/SkeletonLoader';
import { cn } from '../../utils/cn';

// Stat Card Component
interface StatCardProps {
  title: string;
  value: number | string;
  previousValue?: number;
  percentChange?: number;
  icon: React.ReactNode;
  bgColor: string;
  textColor: string;
  iconColor: string;
  link?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  previousValue,
  percentChange,
  icon,
  bgColor,
  textColor,
  iconColor,
  link
}) => {
  const content = (
    <div className={`${bgColor} p-4 sm:p-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-105`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2 sm:p-3 rounded-full ${iconColor}`}>
          <div className="w-5 h-5 sm:w-6 sm:h-6">
            {icon}
          </div>
        </div>
        {percentChange !== undefined && (
          <div className={`flex items-center text-xs sm:text-sm font-medium ${percentChange >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
            {percentChange >= 0 ? (
              <RiArrowUpLine className="mr-1 w-3 h-3 sm:w-4 sm:h-4" />
            ) : (
              <RiArrowDownLine className="mr-1 w-3 h-3 sm:w-4 sm:h-4" />
            )}
            {Math.abs(percentChange)}%
          </div>
        )}
      </div>
      <h2 className={`text-sm sm:text-lg font-medium ${textColor} mb-1`}>{title}</h2>
      <div className="flex items-baseline">
        <p className="text-xl sm:text-3xl font-bold text-secondary-900">
          {typeof value === 'number' && value >= 1000 ? value.toLocaleString() : value}
        </p>
        {previousValue && (
          <p className="ml-2 text-xs sm:text-sm text-secondary-600">
            จาก {previousValue.toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );

  if (link) {
    return <Link to={link}>{content}</Link>;
  }
  return content;
};

// Data Table Component
interface DataTableProps {
  title: string;
  data: any[];
  columns: {key: string, label: string, format?: (value: any, row?: any) => string | React.ReactElement}[];
  action?: {
    label: string;
    link: string;
  };
}

const DataTable: React.FC<DataTableProps> = ({ title, data, columns, action }) => {
  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-200">
        <h2 className="text-lg font-medium text-secondary-900">{title}</h2>
        {action && (
          <Link
            to={action.link}
            className="text-sm font-medium text-info-600 hover:text-info-800 flex items-center transition-colors"
          >
            {action.label}
            <RiArrowRightSLine className="ml-1 w-4 h-4" />
          </Link>
        )}
      </div>
      <div className="overflow-x-auto" tabIndex={0} role="region" aria-label={`ตาราง ${title}`}>
        {data.length > 0 ? (
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-50 border-b-2 border-secondary-200">
              <tr>
                {columns.map((column, index) => (
                  <th
                    key={index}
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-secondary-600 whitespace-nowrap"
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {data.map((row, rowIndex) => (
                <tr key={rowIndex} className="hover:bg-secondary-50 transition-colors">
                  {columns.map((column, colIndex) => {
                    // BUG-S141-DASHTH FIX (sibling): th ไม่มี whitespace-nowrap แต่ td มี →
                    // ชื่อบริษัทไทยยาวดันตารางจนหัวคอลัมน์ถูกบีบและตัดบรรทัดทีละไม่กี่ตัวอักษร
                    // (วัดได้ 97px ที่ 1280px) · ตัดข้อความยาวด้วย ellipsis + title ดูเต็มเมื่อ hover
                    const cell = column.format ? column.format(row[column.key], row) : row[column.key];
                    return (
                    <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm text-secondary-700">
                      <span
                        className="block max-w-[13rem] truncate"
                        title={typeof cell === 'string' || typeof cell === 'number' ? String(cell) : undefined}
                      >
                        {cell}
                      </span>
                    </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState
            icon={<RiFileListLine className="w-12 h-12" />}
            title="ไม่พบข้อมูล"
            description={`ยังไม่มี${title}`}
          />
        )}
      </div>
    </div>
  );
};

const RentalDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { canView, canExport } = usePermission();

  // Permission check - ต้องมีสิทธิ์ดูข้อมูลการเช่า
  useEffect(() => {
    if (!canView('rentals')) {
      navigate('/');
    }
  }, [canView, navigate]);

  // Fetch rental statistics
  const {
    data: statsData,
    isLoading: isLoadingStats,
    // BUG-S2176-001: isLoading เป็น true แค่โหลดครั้งแรก — ตอนกดรีเฟรช (refetch) ต้องดู isFetching
    // ไม่งั้นปุ่มไม่ disabled ระหว่างโหลด (กดซ้ำ = ยิงชุดคำขอซ้ำทั้งชุด) และไม่หมุน
    isFetching: isFetchingStats,
    error: statsError,
    refetch: refetchStats
  } = useQuery({
    queryKey: ['rental-stats'],
    queryFn: async () => {
      try {
        // Get all rental data by fetching multiple pages
        let allRentals: any[] = [];
        let page = 1;
        let hasMore = true;
        
        while (hasMore) {
          const response = await rentalService.getRentals({ 
            page, 
            limit: 100 // Use maximum allowed limit
          });
          
          allRentals = [...allRentals, ...response.items];
          
          // Check if there are more pages
          hasMore = response.items.length === 100 && allRentals.length < (response.total || 0);
          page++;
          
          // Safety check to prevent infinite loop
          if (page > 50) break;
        }
        
        
        const rentals = { items: allRentals, total: allRentals.length };
      
      const now = new Date();
      const thisMonth = now.getMonth();
      const thisYear = now.getFullYear();
      const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
      const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;
      
      // Calculate statistics - handle both upper and lower case status
      const stats = {
        total: rentals.items.length,
        active: rentals.items.filter(r => r.status?.toLowerCase() === 'active').length,
        cancelled: rentals.items.filter(r => r.status?.toLowerCase() === 'cancelled').length,
        renewed: rentals.items.filter(r => r.status?.toLowerCase() === 'renewed').length,
        completed: rentals.items.filter(r => r.status?.toLowerCase() === 'completed').length,
        pending: rentals.items.filter(r => r.status?.toLowerCase() === 'pending_cancellation').length,
        extended: rentals.items.filter(r => r.extension_count && r.extension_count > 0).length,
        totalExtensions: rentals.items.reduce((sum, r) => sum + (r.extension_count || 0), 0),
        thisMonth: rentals.items.filter(r => {
          const date = new Date(r.rental_date);
          return date.getMonth() === thisMonth && date.getFullYear() === thisYear;
        }).length,
        lastMonth: rentals.items.filter(r => {
          const date = new Date(r.rental_date);
          return date.getMonth() === lastMonth && date.getFullYear() === lastMonthYear;
        }).length,
        // BUG-S2169-001: นับเป็นวันปฏิทินให้ตรงกับ ?status=expiring (backend is_expiring_soon)
        expiringSoon: rentals.items.filter(r => rentalExpiryBucket(r) === 'expiring').length,
        expired: rentals.items.filter(r => rentalExpiryBucket(r) === 'expired').length,
        totalRevenue: rentals.items.reduce((sum, r) => sum + (r.total_amount || 0), 0),
        monthlyRevenue: rentals.items
          .filter(r => {
            const date = new Date(r.rental_date);
            return date.getMonth() === thisMonth && date.getFullYear() === thisYear;
          })
          .reduce((sum, r) => sum + (r.total_amount || 0), 0)
      };
      
      // Get recent rentals
      const recentRentals = rentals.items
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 5);
        
      // Get top companies by rental count
      const companyRentals = rentals.items.reduce((acc, rental) => {
        // Handle different possible data structures - prioritize company_name from API
        const companyName = rental.company_name || 
                           rental.company?.name || 
                           rental.site?.company?.name ||
                           'ไม่ระบุบริษัท';
        
        if (!acc[companyName]) {
          acc[companyName] = {
            name: companyName,
            count: 0,
            revenue: 0
          };
        }
        acc[companyName].count++;
        acc[companyName].revenue += rental.total_amount || 0;
        return acc;
      }, {} as Record<string, { name: string; count: number; revenue: number }>);
      
      
      const topCompanies = (Object.values(companyRentals) as Array<{ name: string; count: number; revenue: number }>)
        .filter(company => company.name !== 'ไม่ระบุบริษัท') // Filter out unknown companies
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
      
      return {
        stats,
        recentRentals,
        topCompanies,
        rentals: rentals.items
      };
      } catch (error) {
        console.error('Failed to fetch rental statistics:', error);
        // BUG-S2273-003: เดิมคืนสถิติศูนย์ทั้งหมดเมื่อ API ล้ม → จอขึ้น "การเช่าทั้งหมด 0 / รายได้รวม ฿0 /
        // ยังไม่มีการเช่าล่าสุด" โดยไม่มีข้อความผิดพลาด และสาขา statsError ด้านล่างไม่เคยถูกใช้เลย
        // → โยนต่อให้ useQuery ตั้ง error แล้วแสดงหน้า "เกิดข้อผิดพลาด" + ปุ่มลองใหม่
        throw error;
      }
    },
    refetchInterval: 60000, // Refresh every minute
    retry: 2
  });

  // Calculate derived values
  const stats = statsData?.stats || {
    total: 0,
    active: 0,
    cancelled: 0,
    renewed: 0,
    completed: 0,
    pending: 0,
    extended: 0,
    totalExtensions: 0,
    thisMonth: 0,
    lastMonth: 0,
    expiringSoon: 0,
    expired: 0,
    totalRevenue: 0,
    monthlyRevenue: 0
  };
  
  const growthRate = stats.lastMonth > 0 
    ? Math.round(((stats.thisMonth - stats.lastMonth) / stats.lastMonth) * 100)
    : 0;
    
  const activeRate = stats.total > 0
    ? Math.round((stats.active / stats.total) * 100)
    : 0;
    
  const extensionRate = stats.total > 0
    ? Math.round((stats.extended / stats.total) * 100)
    : 0;

  // Format functions
  const formatCurrency = (value: number) => {
    return value.toLocaleString('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      day: 'numeric',
      month: 'short',
      year: '2-digit'
    });
  };
  
  const formatStatus = (status: string) => {
    const statusMap: { [key: string]: { label: string; className: string } } = {
      draft: { label: 'ร่าง', className: 'text-warning-700' },
      active: { label: 'เช่าใช้อยู่', className: 'text-success-700' },
      pending_cancellation: { label: 'รอการยกเลิก', className: 'text-warning-700' },
      cancelled: { label: 'ยกเลิกแล้ว', className: 'text-danger-700' },
      renewed: { label: 'ต่อการเช่า', className: 'text-info-600' },
      completed: { label: 'คืนแล้ว', className: 'text-secondary-600' },
      pending: { label: 'รอดำเนินการ', className: 'text-yellow-600' }
    };
    // Handle both upper and lower case status
    const normalizedStatus = status?.toLowerCase() || '';
    const statusInfo = statusMap[normalizedStatus] || { label: status, className: 'text-secondary-600' };
    return <span className={statusInfo.className}>{statusInfo.label}</span>;
  };

  // Refresh function
  const handleRefresh = async () => {
    if (isFetchingStats) return;
    // แจ้ง "กำลังโหลด" ก่อนเริ่มโหลด — เดิมเรียกหลัง await จึงขึ้นตอนโหลดเสร็จแล้ว
    showAlert('รีเฟรชข้อมูล', 'กำลังโหลดข้อมูลใหม่...', 'info');
    await refetchStats();
  };

  // Loading state
  if (isLoadingStats) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-secondary-900">ภาพรวมการเช่า</h1>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[...Array(8)].map((_, index) => (
              <StatCardSkeleton key={index} />
            ))}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TableSkeleton />
            <TableSkeleton />
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (statsError) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <EmptyState
            icon={<RiAlertLine className="w-16 h-16" />}
            title="เกิดข้อผิดพลาด"
            description="ไม่สามารถโหลดข้อมูลการเช่าได้"
            action={{
              label: "ลองใหม่",
              onClick: handleRefresh
            }}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
              <RiPieChartLine className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-secondary-900 leading-tight">ภาพรวมการเช่า</h1>
              <p className="text-xs text-secondary-500 mt-0.5">สรุปสถิติและข้อมูลการเช่าอุปกรณ์</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/rentals/list"
              className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
            >
              <RiFileListLine className="w-4 h-4 mr-1.5" />
              รายการเช่า
            </Link>
            <button
              onClick={handleRefresh}
              disabled={isLoadingStats || isFetchingStats}
              aria-busy={isFetchingStats}
              aria-label="รีเฟรชข้อมูล"
              title="รีเฟรชข้อมูล"
              className="inline-flex items-center px-2.5 py-1.5 text-sm text-secondary-600 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors disabled:opacity-50"
            >
              <RiRefreshLine className={`w-4 h-4 ${isLoadingStats || isFetchingStats ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        
        {/* Stat Cards - Row 1: Main Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="การเช่าทั้งหมด"
            value={stats.total}
            icon={<RiFileListLine />}
            bgColor="bg-info-50"
            textColor="text-info-700"
            iconColor="bg-info-200 text-info-600"
            link="/rentals/list"
          />
          <StatCard
            title="เช่าใช้อยู่"
            value={stats.active}
            icon={<RiCheckLine />}
            bgColor="bg-success-50"
            textColor="text-success-700"
            iconColor="bg-success-200 text-success-600"
            link="/rentals/list?status=active"
          />
          <StatCard
            title="ยกเลิกแล้ว"
            value={stats.cancelled}
            icon={<RiCloseLine />}
            bgColor="bg-danger-50"
            textColor="text-danger-700"
            iconColor="bg-danger-200 text-danger-600"
            link="/rentals/list?status=cancelled"
          />
          <StatCard
            title="ต่อการเช่า"
            value={stats.extended}
            icon={<RiRefreshLine />}
            bgColor="bg-purple-50"
            textColor="text-purple-700"
            iconColor="bg-purple-200 text-purple-600"
          />
        </div>
        
        {/* Stat Cards - Row 2: Additional Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="รอการยกเลิก"
            value={stats.pending}
            icon={<RiTimeLine />}
            bgColor="bg-warning-50"
            textColor="text-warning-700"
            iconColor="bg-warning-200 text-warning-600"
            link={`/rentals/list?status=${getRentalStatusUrlParam(RentalStatus.PENDING_CANCELLATION)}`}
          />
          <StatCard
            title="คืนแล้ว"
            value={stats.completed}
            icon={<RiTruckLine />}
            bgColor="bg-secondary-50"
            textColor="text-secondary-700"
            iconColor="bg-secondary-200 text-secondary-600"
            link="/rentals/list?status=completed"
          />
          <StatCard
            title="ใกล้หมดอายุ"
            value={stats.expiringSoon}
            icon={<RiAlertLine />}
            bgColor="bg-warning-50"
            textColor="text-warning-700"
            iconColor="bg-warning-200 text-warning-600"
            link="/rentals/list?status=expiring"
          />
          <StatCard
            title="หมดอายุแล้ว"
            value={stats.expired}
            icon={<RiCalendarLine />}
            bgColor="bg-danger-50"
            textColor="text-danger-700"
            iconColor="bg-danger-200 text-danger-600"
          />
        </div>
        
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="การเช่าเดือนนี้"
            value={stats.thisMonth}
            previousValue={stats.lastMonth}
            percentChange={growthRate}
            icon={<RiLineChartLine />}
            bgColor="bg-info-50"
            textColor="text-info-700"
            iconColor="bg-info-200 text-info-600"
          />
          <StatCard
            title="จำนวนครั้งที่ต่อ"
            value={stats.totalExtensions}
            icon={<RiFileTextLine />}
            bgColor="bg-success-50"
            textColor="text-success-700"
            iconColor="bg-success-200 text-success-600"
          />
          <StatCard
            title="รายได้รวม"
            value={formatCurrency(stats.totalRevenue)}
            icon={<RiMoneyDollarBoxLine />}
            bgColor="bg-success-50"
            textColor="text-success-700"
            iconColor="bg-success-200 text-success-600"
          />
          <StatCard
            title="อัตราการเช่า"
            value={`${activeRate}%`}
            icon={<RiPieChartLine />}
            bgColor="bg-info-50"
            textColor="text-info-700"
            iconColor="bg-info-200 text-info-600"
          />
        </div>
        
        {/* Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Rentals */}
          <DataTable
            title="การเช่าล่าสุด"
            data={statsData?.recentRentals || []}
            columns={[
              { key: 'rental_number', label: 'เลขที่เช่า' },
              { 
                key: 'company_name', 
                label: 'บริษัท',
                format: (value, row) => {
                  // Try multiple ways to get company name
                  const companyName = value || 
                                    row?.company?.name || 
                                    row?.company_name || 
                                    row?.site?.company?.name ||
                                    '-';
                  return companyName;
                }
              },
              { 
                key: 'status', 
                label: 'สถานะ',
                format: (value) => formatStatus(value)
              },
              { 
                key: 'rental_date', 
                label: 'วันที่เช่า',
                format: (value) => formatDate(value)
              }
            ]}
            action={{
              label: 'ดูทั้งหมด',
              link: '/rentals/list'
            }}
          />
          
          {/* Top Companies */}
          <DataTable
            title="บริษัทที่เช่ามากที่สุด"
            data={statsData?.topCompanies || []}
            columns={[
              { key: 'name', label: 'ชื่อบริษัท' },
              { key: 'count', label: 'จำนวนครั้ง' },
              { 
                key: 'revenue', 
                label: 'รายได้รวม',
                format: (value) => formatCurrency(value || 0)
              }
            ]}
            // G-12: ลิงก์ข้าม module ต้องมีสิทธิ์ปลายทาง (reports.view) — ไม่มีสิทธิ์ = ไม่แสดงปุ่ม
            action={canView('reports') ? {
              label: 'ดูรายงาน',
              link: '/reports/rentals'
            } : undefined}
          />
        </div>
      </div>
    </Layout>
  );
};

export default RentalDashboard;